import * as R from "ramda";
import React from "react";

import * as STANDARD_MODULES from "@sessionboard/sessionboard-value-types/src/constants/standard-modules";

import CoreLayout from "layouts/CoreLayout";
import { Route } from "react-router";

import DefaultNotFound from "components/Platform/NotFound";

import userView from "components/Global/Security/AuthenticatedRoute";

const makeChunk = (root, components) =>
  R.reduce(
    (acc, name) => ({
      ...acc,
      [name]: location =>
        root(location).then(component => component.default[name])
    }),
    {},
    components
  );

const canUserView = (f, ...args) => location =>
  f(location).then(component => userView(component, ...args));

const Platform = makeChunk(
  () => import(/* webpackChunkName: "platform" */ "./Platform"),
  ["HomeView", "PlatformView"]
);

const Intake = makeChunk(
  () => import(/* webpackChunkName: "intake" */ "./Intake"),
  ["IntakeForm", "IntakeLogin", "IntakeCallback"]
);

const CallForPapers = makeChunk(
  () => import(/* webpackChunkName: "callforpapers" */ "./CallForPapers"),
  ["CallForPapersForm", "CallForPapersLogin", "CallForPapersCallback"]
);

const ApplicationForm = makeChunk(
  () => import(/* webpackChunkName: "callforpapers" */ "./ApplicationForm"),
  ["ApplicationForm", "ApplicationFormLogin", "ApplicationFormCallback"]
);

const FollowupForm = makeChunk(
  () => import(/* webpackChunkName: "callforpapers" */ "./FollowupForm"),
  ["FollowupForm"]
);

const Auth = makeChunk(() => import(/* webpackChunkName: "auth" */ "./Auth"), [
  "SignupStart",
  "SignupComplete",
  "SignupResend",
  "SignupVerificationConfirm",
  "LoginView",
  "CallbackView",
  "LogoutView",
  "LogoutCallbackView",
  "ForgotPasswordHandler",
  "ResetPasswordHandler",
  "JoinVirtualUserHandler",
  "LoggedOutView",
  "SignupOnboard",
  "SSOView"
]);

const Portal = makeChunk(
  () => import(/* webpackChunkName: "portal" */ "./Portal"),
  [
    "LoggedInView",
    "LoggedOutView",
    "PortalWrapper",
    "ManagePortals",
    "PortalHomeRouter",
    "PortalSidebar",
    "PortalDocuments",
    "PortalSwitch",
    "PortalLogin",
    "PortalLoginCallback",
    "PortalPage",
    "PortalSession",
    "SSOLink"
  ]
);

// new light platform

const OrgLight = makeChunk(
  () => import(/* webpackChunkName: "orglight" */ "./OrgLight"),
  [
    "OrgLightGate",
    "OrgLightLayout",
    "OrgLightDashboard",
    "OrgLightOrgEvents",
    "OrgLightPeopleNew",
    "OrgLightSpeakers",
    "OrgLightContactProfile",
    "OrgLightTags",
    "OrgLightSegments",
    "OrgLightFields",
    "OrgLightSettings",
    "OrgLightDedupeContact",
    "OrgLightWebHooks",
    "OrgLightContent",
    "OrgLightContentProfile",
    "OrgLightHistory"
  ]
);

const EventLight = makeChunk(
  () => import(/* webpackChunkName: "eventlight" */ "./EventLight"),
  [
    "EventLightGate",
    "EventLightLayout",
    "EventLightDashboard",
    "EventLightDetails",
    "EventLightContent",
    "EventLightReports",
    "EventLightDocuments",
    "EventLightTeam",
    "EventLightSessions",
    "EventLightSessionsPage",
    "EventLightEditDocument",
    "EventLightContentProfile",
    "EventLightApplications",
    "EventLightApplicationSubmissions",
    "EventLightPortals",
    "EventLightPortalWrapper",
    "EventLightFileRequest",
    "EventLightSettings",
    "EventLightExhibitors",
    "EventLightSponsors",
    "EventLightSpeakers",
    "EventLightContacts",
    "EventLightHistory",
    "EventLightContactProfile",
    "EventLightContactGroup",
    "EventLightEvaluationPlanRedirect",
    "EventLightSponsorsSettingsTiers",
    "EventLightFields",
    "EventLightTags",
    "EventLightWorkflows",
    "EventLightWorkflowsBuilder",
    "EventLightEvaluation",
    "EventLightEvaluationPlans",
    "EventLightEvaluationPlansEdit",
    "EventLightEvaluationSummary"
  ]
);

// new light platform

const Account = makeChunk(
  () => import(/* webpackChunkName: "account" */ "./Account"),
  ["SettingsSettingsHandler", "SettingsHandler", "Profile"]
);

export default (
  <Route component={CoreLayout}>
    <Route
      name="SignupVerificationConfirm"
      path="/account/confirm/:verificationHash"
      getComponent={Auth.SignupVerificationConfirm}
    />
    <Route
      name="SignupComplete"
      path="/signup/complete"
      getComponent={Auth.SignupComplete}
    />
    <Route
      name="SignupResend"
      path="/signup/resend"
      getComponent={Auth.SignupResend}
    />
    <Route name="Signup" path="/signup" getComponent={Auth.SignupStart} />
    <Route
      name="Signup"
      path="/signup/:template"
      getComponent={Auth.SignupStart}
    />
    <Route
      name="SignupVerificationOnboard"
      path="/account/onboard"
      getComponent={Auth.SignupOnboard}
    />
    <Route name="LoggedOutView" getComponent={Auth.LoggedOutView}>
      <Route name="LoginView" getComponent={Auth.LoginView} path="/" />
      <Route
        name="LoginView"
        getComponent={Auth.LoginView}
        path="/auth/login"
      />
      <Route name="LoginView" getComponent={Auth.LoginView} path="/login" />
      <Route name="LogoutView" getComponent={Auth.LogoutView} path="/logout" />
      <Route
        name="LogoutCallbackView"
        getComponent={Auth.LogoutCallbackView}
        path="/logout/callback"
      />
      <Route
        name="CallbackView"
        getComponent={Auth.CallbackView}
        path="/auth/callback"
      />
      <Route
        name="forgotPassword"
        path="/forgot"
        getComponent={Auth.ForgotPasswordHandler}
      />
      <Route
        name="resetPassword"
        path="/reset/:hash"
        getComponent={Auth.ResetPasswordHandler}
      />
      <Route name="SSOView" path="/sso" getComponent={Auth.SSOView} />
      <Route
        name="joinVirtualUser"
        path="/join/vu/:virtualUserHash"
        getComponent={Auth.JoinVirtualUserHandler}
      />

      {/**  */}
      <Route
        name="intakeForm"
        path="/intake/:eventSlug/:intakeId"
        getComponent={Intake.IntakeForm}
      />
      <Route
        name="intakeCallback"
        path="/intake-callback"
        getComponent={Intake.IntakeForm}
      />
      <Route
        name="intakeLogin"
        path="/intake/:eventSlug/:intakeId/logon"
        getComponent={Intake.IntakeForm}
      />

      {/**  */}
      <Route
        name="callForPapersForm"
        path="/submit/:eventSlug/:formId(/submissions/:submissionId)(/step/:stepId)"
        getComponent={CallForPapers.CallForPapersForm}
      />
      <Route
        name="callForPapersCallback"
        path="/submit-callback"
        getComponent={CallForPapers.CallForPapersForm}
      />
      <Route
        name="callForPapersLogin"
        path="/submit/:eventSlug/:formId/logon"
        getComponent={CallForPapers.CallForPapersForm}
      />

      {/**  */}
      <Route
        name="followupForm"
        path="/followup-forms/:formSlug/:formId(/submissions/:submissionId)(/preview)(/enabledNewPortal/:enabledNewPortal)"
        getComponent={FollowupForm.FollowupForm}
      />

      {/**  */}
      <Route
        name="applicationForm"
        path="/applications/:formSlug/:formId(/submissions/:submissionId)"
        getComponent={ApplicationForm.ApplicationForm}
      />
      <Route
        name="applicationFormCallback"
        path="/applications-callback"
        getComponent={ApplicationForm.ApplicationForm}
      />
      <Route
        name="applicationFormLogin"
        path="/applications/:formSlug/:formId/logon"
        getComponent={ApplicationForm.ApplicationForm}
      />
    </Route>

    <Route
      name="ssoLink"
      path="/sso-link/event/:eventUUID/account/:accountId"
      getComponent={Portal.SSOLink}
    />

    <Route name="PortalV2_LoggedOutView" getComponent={Portal.LoggedOutView}>
      <Route name="portalV2_Portal" getComponent={Portal.PortalWrapper}>
        <Route
          name="portalV2_PortalLogin"
          path="/portal-login/:portalSlug/:portalId"
          getComponent={Portal.PortalLogin}
        />
        <Route
          name="portalV2_PortalLoginCallback"
          path="/portal-login-callback"
          getComponent={Portal.PortalLoginCallback}
        />
      </Route>
    </Route>

    <Route name="PortalV2_LoggedInView" getComponent={Portal.LoggedInView}>
      <Route name="portalV2_Portal" getComponent={Portal.PortalWrapper}>
        <Route
          name="portalV2_PortalSwitch"
          getComponent={Portal.PortalSwitch}
          path="/portal-switch/:portalSlug/:portalId"
        />
        <Route
          name="portalV2_PortalSidebar"
          getComponent={Portal.PortalSidebar}
        >
          {/** new portal */}
          <Route
            name="portalV2_PortalLanding"
            getComponent={Portal.PortalHomeRouter}
            path="/portals/:portalSlug/:portalId"
          />
          <Route
            name="PortalSessions"
            getComponent={Portal.PortalHomeRouter}
            path="/portals/:portalSlug/:portalId/sessions"
          />
          <Route
            name="PortalResources"
            getComponent={Portal.PortalHomeRouter}
            path="/portals/:portalSlug/:portalId/resources"
          />
          <Route
            name="PortalResourcesResource"
            getComponent={Portal.PortalHomeRouter}
            path="/portals/:portalSlug/:portalId/resources/:resourceId"
          />
          <Route
            name="PortalCalendar"
            getComponent={Portal.PortalHomeRouter}
            path="/portals/:portalSlug/:portalId/calendar"
          />
          <Route
            name="PortalProfile"
            getComponent={Portal.PortalHomeRouter}
            path="/portals/:portalSlug/:portalId/profile"
          />
          <Route
            name="PortalMessages"
            getComponent={Portal.PortalHomeRouter}
            path="/portals/:portalSlug/:portalId/messages"
          />
          <Route
            name="PortalHistory"
            getComponent={Portal.PortalHomeRouter}
            path="/portals/:portalSlug/:portalId/history"
          />
          <Route
            name="PortalTasks"
            getComponent={Portal.PortalHomeRouter}
            path="/portals/:portalSlug/:portalId/tasks"
          />

          {/** end new portal */}

          <Route
            name="portalV2_PortalDocumentsRequestModal"
            getComponent={Portal.PortalDocuments}
            path="/portals/:portalSlug/:portalId/documents/request/:requestId"
          />
          <Route
            name="portalV2_PortalPage"
            path="/portals/:portalSlug/:portalId/pages/:pageId"
            getComponent={Portal.PortalPage}
          />
          <Route
            name="portalV2_PortalSession"
            path="/portals/:portalSlug/:portalId/session/:sessionId"
            getComponent={Portal.PortalSession}
          />
        </Route>
      </Route>
    </Route>

    <Route
      name="OrgLightGate"
      path="/org-light/:orgId"
      getComponent={OrgLight.OrgLightGate}
    >
      <Route
        name="OrgLightDashboard"
        path="dashboard"
        getComponent={OrgLight.OrgLightDashboard}
      />

      <Route
        name="OrgLightOrgEvents"
        path="events"
        getComponent={OrgLight.OrgLightOrgEvents}
      />

      <Route
        name="OrgLightPermissions"
        path="settings/team/permissions"
        getComponent={OrgLight.OrgLightSettings}
      />

      <Route
        name="OrgLightContent"
        path="files"
        // getComponent={canUserView(
        //   OrgLight.OrgLightContent,
        //   STANDARD_MODULES.content.id,
        //   "read"
        // )}
        getComponent={OrgLight.OrgLightContent}
      />

      <Route
        name="OrgLightContentProfile"
        path="files/:fileId"
        // getComponent={canUserView(
        //   EventLight.EventLightContentProfile,
        //   STANDARD_MODULES.content.id,
        //   "read"
        // )}
        getComponent={OrgLight.OrgLightContentProfile}
      />

      <Route
        name="OrgLightHistory"
        path="history/:historyType"
        getComponent={OrgLight.OrgLightHistory}
      />

      <Route
        name="OrgLightTags"
        path="tags"
        getComponent={OrgLight.OrgLightTags}
      />
      <Route
        name="OrgLightSegments"
        path="segments"
        getComponent={OrgLight.OrgLightSegments}
      />
      <Route
        name="OrgLightFields"
        path="fields(/:moduleId)"
        getComponent={OrgLight.OrgLightFields}
      />
      <Route
        name="OrgLightSettingsDetails"
        path="settings"
        getComponent={OrgLight.OrgLightSettings}
      />
      <Route
        name="OrgLightSettingsTeam"
        path="settings/team"
        getComponent={OrgLight.OrgLightSettings}
      />
      <Route
        name="OrgLightSettingsTemplates"
        path="settings/templates"
        getComponent={OrgLight.OrgLightSettings}
      />
      <Route
        name="OrgLightSettingsIntegrations"
        path="settings/integrations"
        getComponent={OrgLight.OrgLightSettings}
      />
      <Route
        name="OrgLightSettingsApiTokens"
        path="settings/api-tokens"
        getComponent={OrgLight.OrgLightSettings}
      />
      <Route
        name="OrgLightSettingsSSO"
        path="settings/sso"
        getComponent={OrgLight.OrgLightSettings}
      />

      {/** */}

      <Route
        name="OrgLightSpeakers"
        path="speakers"
        getComponent={OrgLight.OrgLightSpeakers}
      />
      <Route
        name="OrgLightSegmentRecords"
        path="speakers/segments/:segmentId"
        getComponent={OrgLight.OrgLightSpeakers}
      />

      <Route
        name="OrgLightPeopleNew"
        path="people"
        getComponent={OrgLight.OrgLightPeopleNew}
      />
      <Route
        name="OrgLightSegmentRecords"
        path="people/segments/:segmentId"
        getComponent={OrgLight.OrgLightPeopleNew}
      />

      <Route getComponent={OrgLight.OrgLightContactProfile}>
        <Route name="OrgLightContactProfile" path="contact/:recordId" />
        <Route
          name="OrgLightContactHistory"
          path="contact/:recordId/history/:historyType"
        />
        <Route name="OrgLightContactFiles" path="contact/:recordId/files" />
        <Route name="OrgLightContactEdit" path="contact/:recordId/edit" />
      </Route>

      <Route
        name="OrgLightDedupeContact"
        path="contacts/dedupe/:contactId"
        getComponent={OrgLight.OrgLightDedupeContact}
      />

      {/** */}
      {/* <Route
        name="OrgLightWebHooks"
        path="webhooks"
        getComponent={OrgLight.OrgLightWebHooks}
      /> */}

      <Route
        name="OrgLightSingleSignOn"
        path="single-sign-on"
        getComponent={OrgLight.OrgLightSSO}
      />

      {/** */}
    </Route>

    <Route
      name="eventLightGate"
      path="/event-light/:eventId"
      getComponent={EventLight.EventLightGate}
    >
      <Route
        name="EventLightDashboard"
        path="dashboard"
        getComponent={EventLight.EventLightDashboard}
      />
      <Route
        name="EventLightDetails"
        path="settings/details"
        getComponent={canUserView(
          EventLight.EventLightSettings,
          STANDARD_MODULES.settings.id,
          "update"
        )}
      />

      {/* */}

      <Route
        name="EventLightContent"
        path="content"
        getComponent={canUserView(
          EventLight.EventLightContent,
          STANDARD_MODULES.content.id,
          "read"
        )}
      />

      {/* */}

      <Route
        name="EventLightSponsors"
        path="sponsors"
        getComponent={canUserView(
          EventLight.EventLightSponsors,
          STANDARD_MODULES.sponsors.id,
          "read"
        )}
      />
      <Route
        name="EventLightSponsorsPortal"
        path="sponsors/portal"
        getComponent={canUserView(
          EventLight.EventLightSponsors,
          STANDARD_MODULES.sponsors.id,
          "read"
        )}
      />
      <Route
        name="EventLightSponsorsSettingsTiers"
        path="sponsors/settings/tiers"
        getComponent={canUserView(
          EventLight.EventLightSponsors,
          STANDARD_MODULES.sponsors.id,
          "read"
        )}
      />

      <Route
        name="EventLightExhibitors"
        path="exhibitors"
        getComponent={canUserView(
          EventLight.EventLightExhibitors,
          STANDARD_MODULES.exhibitors.id,
          "read"
        )}
      />
      <Route
        name="EventLightExhibitorsPortal"
        path="exhibitors/portal"
        getComponent={canUserView(
          EventLight.EventLightExhibitors,
          STANDARD_MODULES.exhibitors.id,
          "read"
        )}
      />

      <Route
        name="EventLightSpeakers"
        path="speakers"
        getComponent={canUserView(
          EventLight.EventLightSpeakers,
          STANDARD_MODULES.speakers.id,
          "read"
        )}
      />
      <Route
        name="EventLightSpeakersPortal"
        path="speakers/portal"
        getComponent={canUserView(
          EventLight.EventLightSpeakers,
          STANDARD_MODULES.speakers.id,
          "read"
        )}
      />

      <Route
        name="EventLightContacts"
        path="contacts"
        getComponent={canUserView(
          EventLight.EventLightContacts,
          STANDARD_MODULES.contacts.id,
          "read"
        )}
      />

      <Route
        name="EventLightEvaluationPlanRedirect"
        path="sessions/settings/evaluation"
        getComponent={EventLight.EventLightEvaluationPlanRedirect}
      />

      {/** */}

      <Route
        name="EventLightReports"
        path="reports"
        getComponent={canUserView(
          EventLight.EventLightReports,
          STANDARD_MODULES.reports.id,
          "read"
        )}
      />
      <Route
        name="EventLightDocuments"
        path="documents"
        getComponent={canUserView(
          EventLight.EventLightDocuments,
          "documents", //STANDARD_MODULES.documents.id,
          "read"
        )}
        // getComponent={canUserView(
        //   EventLight.EventLightDocuments,
        //   STANDARD_MODULES.documents.id,
        //   "read"
        // )}
      />
      <Route
        name="EventLightTeam"
        path="team"
        getComponent={canUserView(
          EventLight.EventLightTeam,
          STANDARD_MODULES.settings.id,
          "manage_users"
        )}
      />
      <Route
        name="EventLightPermissions"
        path="team/permissions"
        getComponent={canUserView(
          EventLight.EventLightTeam,
          STANDARD_MODULES.settings.id,
          "manage_permissions"
        )}
      />

      {/* */}

      <Route
        name="EventLightSessionsForm"
        path="sessions/form"
        getComponent={canUserView(
          EventLight.EventLightSessions,
          STANDARD_MODULES.sessionForms.id,
          "read"
        )}
      />
      <Route
        name="EventLightSessionsSubmissions"
        path="sessions/submissions"
        getComponent={canUserView(
          EventLight.EventLightSessions,
          STANDARD_MODULES.sessions.id,
          "read"
        )}
      />
      <Route
        name="EventLightSessionsForms"
        path="sessions/forms"
        getComponent={canUserView(
          EventLight.EventLightSessions,
          STANDARD_MODULES.sessionForms.id,
          "read"
        )}
      />
      <Route
        name="EventLightSessionsEvaluation"
        path="sessions/evaluation"
        getComponent={canUserView(
          EventLight.EventLightSessions,
          STANDARD_MODULES.evaluationPlans.id,
          "read"
        )}
      />
      <Route
        name="EventLightSessionsReview"
        path="sessions/review/:planId(/session/:sessionId)"
        getComponent={canUserView(
          EventLight.EventLightSessions,
          STANDARD_MODULES.evaluationPlans.id,
          "read"
        )}
      />
      <Route
        name="EventLightSessionsMonitor"
        path="sessions/monitor/:planId(/session/:sessionId)"
        getComponent={canUserView(
          EventLight.EventLightSessions,
          STANDARD_MODULES.evaluationPlans.id,
          "read"
        )}
      />
      <Route
        name="EventLightSessionsSettingsEmbeds"
        path="sessions/agenda/embeds"
        getComponent={canUserView(
          EventLight.EventLightSessions,
          STANDARD_MODULES.embeds.id,
          "read"
        )}
      />
      <Route
        name="EventLightSessionsConflicts"
        path="sessions/agenda/conflicts"
        getComponent={canUserView(
          EventLight.EventLightSessions,
          STANDARD_MODULES.agenda.id,
          "read"
        )}
      />
      <Route
        name="EventLightSessionsSchedule"
        path="sessions/agenda(/:calendarView)"
        getComponent={canUserView(
          EventLight.EventLightSessions,
          STANDARD_MODULES.agenda.id,
          "read"
        )}
      />
      <Route
        name="EventLightSessionsSchedule"
        path="sessions/agenda/settings"
        getComponent={canUserView(
          EventLight.EventLightSessions,
          STANDARD_MODULES.agenda.id,
          "read"
        )}
      />

      <Route
        name="EventLightSessionsSettings"
        path="sessions/settings"
        getComponent={canUserView(
          EventLight.EventLightSessions,
          STANDARD_MODULES.sessionSettings.id,
          "read"
        )}
      />
      <Route
        name="EventLightSessionsSettingsRooms"
        path="sessions/settings/rooms"
        getComponent={canUserView(
          EventLight.EventLightSessions,
          STANDARD_MODULES.sessionSettings.id,
          "read"
        )}
      />

      <Route
        name="EventLightSessionsSettingsTracks"
        path="sessions/settings/tracks"
        getComponent={canUserView(
          EventLight.EventLightSessions,
          STANDARD_MODULES.sessionSettings.id,
          "read"
        )}
      />
      <Route
        name="EventLightSessionsSettingsLanguages"
        path="sessions/settings/languages"
        getComponent={canUserView(
          EventLight.EventLightSessions,
          STANDARD_MODULES.sessionSettings.id,
          "read"
        )}
      />
      <Route
        name="EventLightSessionsSettingsContent"
        path="sessions/settings/content"
        getComponent={canUserView(
          EventLight.EventLightSessions,
          STANDARD_MODULES.sessionSettings.id,
          "read"
        )}
      />
      <Route
        name="EventLightSessionsSettingsStatuses"
        path="sessions/settings/statuses"
        getComponent={canUserView(
          EventLight.EventLightSessions,
          STANDARD_MODULES.sessionSettings.id,
          "read"
        )}
      />
      <Route
        name="EventLightSessionsSettingsFormats"
        path="sessions/settings/formats"
        getComponent={canUserView(
          EventLight.EventLightSessions,
          STANDARD_MODULES.sessionSettings.id,
          "read"
        )}
      />
      <Route
        name="EventLightSessionsSettingsLevels"
        path="sessions/settings/levels"
        getComponent={canUserView(
          EventLight.EventLightSessions,
          STANDARD_MODULES.sessionSettings.id,
          "read"
        )}
      />
      <Route
        name="EventLightSessionsSettingsEvaluation"
        path="sessions/evaluation-plans"
        getComponent={canUserView(
          EventLight.EventLightSessions,
          STANDARD_MODULES.evaluationPlans.id,
          "read"
        )}
      />

      <Route
        name="EventLightContentProfile"
        path="files/:fileId"
        getComponent={canUserView(
          EventLight.EventLightContentProfile,
          STANDARD_MODULES.content.id,
          "read"
        )}
      />

      <Route
        name="EventLightEditDocument"
        path="files/:fileId/edit"
        getComponent={canUserView(
          EventLight.EventLightEditDocument,
          STANDARD_MODULES.content.id,
          "update"
        )}
      />

      {/*
       *  Session Page
       */}
      <Route
        name="EventLightSessionsPage"
        path="sessions/:sessionId"
        getComponent={EventLight.EventLightSessionsPage}
      />
      <Route
        name="EventLightSessionsPageDetails"
        path="sessions/:sessionId/details"
        getComponent={EventLight.EventLightSessionsPage}
      />
      <Route
        name="EventLightSessionsPageParticipants"
        path="sessions/:sessionId/participants"
        getComponent={EventLight.EventLightSessionsPage}
      />
      <Route
        name="EventLightSessionsPageFileContent"
        path="sessions/:sessionId/file-content"
        getComponent={EventLight.EventLightSessionsPage}
      />
      <Route
        name="EventLightSessionsPageSubsessions"
        path="sessions/:sessionId/subsessions"
        getComponent={EventLight.EventLightSessionsPage}
      />

      {/* */}

      {/** View list of my evaluations */}
      <Route
        name="EventLightEvaluation"
        path="my-evaluations"
        getComponent={canUserView(
          EventLight.EventLightEvaluation,
          STANDARD_MODULES.evaluationPlans.id,
          "read"
        )}
      />

      {/** View list of evaluation plans */}
      <Route
        name="EventLightEvaluationPlans"
        path="evaluation-plans"
        getComponent={canUserView(
          EventLight.EventLightEvaluation,
          STANDARD_MODULES.evaluationPlans.id,
          "read"
        )}
      />

      {/** Create or Edit Evaluation Plan */}
      <Route
        name="EventLightEvaluationPlansCreate"
        path="evaluation-plans/add"
        getComponent={canUserView(
          EventLight.EventLightEvaluationPlansEdit,
          STANDARD_MODULES.evaluationPlans.id,
          "update"
        )}
      />
      <Route
        name="EventLightEvaluationPlansEdit"
        path="evaluation-plans/edit/:planId(/step/:stepId)"
        getComponent={canUserView(
          EventLight.EventLightEvaluationPlansEdit,
          STANDARD_MODULES.evaluationPlans.id,
          "update"
        )}
      />

      {/** Review Evaluation Plan */}
      <Route
        name="EventLightEvaluationReview"
        path="evaluation/review/:planId(/session/:sessionId)"
        getComponent={canUserView(
          EventLight.EventLightEvaluation,
          STANDARD_MODULES.evaluationPlans.id,
          "read"
        )}
      />

      {/** Monitor Evaluation Plan */}
      <Route
        name="EventLightEvaluationMonitor"
        path="evaluation/monitor/:planId(/session/:sessionId)"
        getComponent={canUserView(
          EventLight.EventLightEvaluation,
          STANDARD_MODULES.evaluationPlans.id,
          "read"
        )}
      />

      {/** View evaluation summary */}
      <Route
        name="EventLightEvaluationSummary"
        path="evaluation-summary"
        getComponent={canUserView(
          EventLight.EventLightEvaluationSummary,
          STANDARD_MODULES.evaluationPlans.id,
          "read"
        )}
      />

      {/* */}

      <Route
        name="EventLightApplications"
        path="applications"
        getComponent={canUserView(
          EventLight.EventLightApplications,
          STANDARD_MODULES.applications.id,
          "read"
        )}
      />
      <Route
        name="EventLightApplicationSubmissions"
        path="applications/:formId/submissions"
        getComponent={canUserView(
          EventLight.EventLightApplicationSubmissions,
          STANDARD_MODULES.applications.id,
          "read"
        )}
      />

      {/* */}

      <Route
        name="EventLightPortals"
        path="portals"
        getComponent={canUserView(
          EventLight.EventLightPortals,
          STANDARD_MODULES.portal.id,
          "read"
        )}
      />
      <Route
        name="EventLightPortalsConfigSets"
        path="portals/config-sets(/:configSetType)"
        getComponent={canUserView(
          EventLight.EventLightPortals,
          STANDARD_MODULES.portal.id,
          "read"
        )}
      />
      <Route
        name="EventLightPortalsConfigSetsConfigSetFilters"
        path="portals/config-sets/:configSetId/filters"
        getComponent={canUserView(
          EventLight.EventLightPortalWrapper,
          STANDARD_MODULES.portal.id,
          "read"
        )}
      />
      <Route
        name="EventLightPortalsConfigSetsConfigSet"
        path="portals/config-sets/:configSetId/customize"
        getComponent={canUserView(
          EventLight.EventLightPortalWrapper,
          STANDARD_MODULES.portal.id,
          "read"
        )}
      />
      <Route
        name="EventLightPortalsConfigSetsConfigSetConfiguration"
        path="portals/config-sets/:configSetId/configuration"
        getComponent={canUserView(
          EventLight.EventLightPortalWrapper,
          STANDARD_MODULES.portal.id,
          "read"
        )}
      />
      <Route
        name="EventLightPortalsConfigSetsConfigSetAppearance"
        path="portals/config-sets/:configSetId/appearance"
        getComponent={canUserView(
          EventLight.EventLightPortalWrapper,
          STANDARD_MODULES.portal.id,
          "read"
        )}
      />
      <Route
        name="EventLightPortalsConfigSetsConfigSetAppearanceGeneral"
        path="portals/config-sets/:configSetId/appearance/general"
        getComponent={canUserView(
          EventLight.EventLightPortalWrapper,
          STANDARD_MODULES.portal.id,
          "read"
        )}
      />
      <Route
        name="EventLightPortalsConfigSetsConfigSetAppearanceWidgets"
        path="portals/config-sets/:configSetId/appearance/widgets"
        getComponent={canUserView(
          EventLight.EventLightPortalWrapper,
          STANDARD_MODULES.portal.id,
          "read"
        )}
      />
      <Route
        name="EventLightPortalsConfigSetsConfigSetAppearanceManageFields"
        path="portals/config-sets/:configSetId/appearance/manage-fields"
        getComponent={canUserView(
          EventLight.EventLightPortalWrapper,
          STANDARD_MODULES.portal.id,
          "read"
        )}
      />
      <Route
        name="EventLightPortalsPages"
        path="portals/pages"
        getComponent={canUserView(
          EventLight.EventLightPortals,
          STANDARD_MODULES.portalTasks.id,
          "read"
        )}
      />
      <Route
        name="EventLightPortalsWikiPageEditor"
        path="portals/wikiPage/:wikiPageId"
        getComponent={canUserView(
          EventLight.EventLightPortals,
          STANDARD_MODULES.portalTasks.id,
          "read"
        )}
      />
      <Route
        name="EventLightPortalsTasks"
        path="portals/tasks"
        getComponent={canUserView(
          EventLight.EventLightPortals,
          STANDARD_MODULES.portalTasks.id,
          "read"
        )}
      />
      <Route
        name="EventLightContent"
        path="portals/content"
        getComponent={canUserView(
          EventLight.EventLightPortals,
          STANDARD_MODULES.portalTasks.id,
          "read"
        )}
      />
      <Route
        name="EventLightPortalsFileRequests"
        path="portals/file-requests"
        getComponent={canUserView(
          EventLight.EventLightPortals,
          STANDARD_MODULES.portalTasks.id,
          "read"
        )}
      />
      <Route
        name="EventLightPortalsFileRequestsResults"
        path="file-requests/:fileRequestId/results"
        getComponent={canUserView(
          EventLight.EventLightFileRequest,
          STANDARD_MODULES.portalTasks.id,
          "read"
        )}
      />
      <Route
        name="EventLightPortalsOldForms"
        path="portals/old-forms"
        getComponent={canUserView(
          EventLight.EventLightPortals,
          STANDARD_MODULES.portalTasks.id,
          "read"
        )}
      />
      <Route
        name="EventLightPortalsFollowupForms"
        path="portals/forms"
        getComponent={canUserView(
          EventLight.EventLightPortals,
          STANDARD_MODULES.portalTasks.id,
          "read"
        )}
      />
      <Route
        name="EventLightPortalsFollowupFormSubmissions"
        path="portals/forms/:formId/submissions"
        getComponent={canUserView(
          EventLight.EventLightPortals,
          STANDARD_MODULES.portalTasks.id,
          "read"
        )}
      />
      <Route
        name="EventLightPortalsFiles"
        path="portals/files"
        getComponent={canUserView(
          EventLight.EventLightPortals,
          STANDARD_MODULES.portalTasks.id,
          "read"
        )}
      />

      {/* Emails */}
      <Route
        name="EventLightHistory"
        path="history/:historyType"
        getComponent={canUserView(EventLight.EventLightHistory, null, null, [
          {
            moduleId: STANDARD_MODULES.history.id,
            action: "read"
          },
          {
            moduleId: STANDARD_MODULES.emails.id,
            action: "read"
          }
        ])}
      />

      {/* <Route
        name="EventLightContentExport"
        path="content/export"
        getComponent={canUserView(
          EventLight.EventLightHistory,
          STANDARD_MODULES.histoy.id,
          "read"
        )}
      /> */}

      {/* */}

      <Route
        name="EventLightSettings"
        path="settings"
        getComponent={canUserView(
          EventLight.EventLightSettings,
          STANDARD_MODULES.settings.id,
          "update"
        )}
      />

      <Route
        name="EventLightSettingsEmailTemplates"
        path="settings/email-templates"
        getComponent={canUserView(
          EventLight.EventLightSettings,
          STANDARD_MODULES.emails.id,
          "manage_email_templates"
        )}
      />

      <Route
        name="EventLightSettingsPortalsLogIn"
        path="settings/portals/log-in-page"
        getComponent={canUserView(
          EventLight.EventLightSettings,
          STANDARD_MODULES.settings.id,
          "update"
        )}
      />

      <Route
        name="EventLightSettingsPortalsHomepageGeneral"
        path="settings/portals/appearance/general"
        getComponent={canUserView(
          EventLight.EventLightSettings,
          STANDARD_MODULES.settings.id,
          "update"
        )}
      />

      <Route
        name="EventLightSettingsLoginSettings"
        path="settings/login-settings"
        getComponent={canUserView(
          EventLight.EventLightSettings,
          STANDARD_MODULES.settings.id,
          "update"
        )}
      />

      <Route
        name="EventLightSettingsSubmissionFormsAppearance"
        path="settings/submission-forms/appearance"
        getComponent={canUserView(
          EventLight.EventLightSettings,
          STANDARD_MODULES.settings.id,
          "update"
        )}
      />
      <Route
        name="EventLightSettingsSubmissionFormsTypography"
        path="settings/submission-forms/typography"
        getComponent={canUserView(
          EventLight.EventLightSettings,
          STANDARD_MODULES.settings.id,
          "update"
        )}
      />
      <Route
        name="EventLightSettingsSubmissionFormsContent"
        path="settings/submission-forms/content"
        getComponent={canUserView(
          EventLight.EventLightSettings,
          STANDARD_MODULES.settings.id,
          "update"
        )}
      />

      {/* Webhooks */}
      <Route
        name="EventLightSettingsIntegrations"
        path="settings/integrations"
        getComponent={canUserView(
          EventLight.EventLightSettings,
          STANDARD_MODULES.settings.id,
          "update"
        )}
      />

      <Route
        name="EventLightSettingsZapier"
        path="settings/integrations/zapier"
        getComponent={canUserView(
          EventLight.EventLightSettings,
          STANDARD_MODULES.settings.id,
          "update"
        )}
      />

      <Route
        name="EventLightSettingsWebhooks"
        path="settings/integrations/webhooks"
        getComponent={canUserView(
          EventLight.EventLightSettings,
          STANDARD_MODULES.settings.id,
          "update"
        )}
      />

      {/* Fields */}
      <Route
        name="EventLightFields"
        path="fields(/:moduleId)"
        getComponent={canUserView(EventLight.EventLightFields, null, null, [
          {
            moduleId: STANDARD_MODULES.contacts.id,
            action: "manage_fields"
          },
          {
            moduleId: STANDARD_MODULES.speakers.id,
            action: "manage_fields"
          },
          {
            moduleId: STANDARD_MODULES.exhibitors.id,
            action: "manage_fields"
          },
          {
            moduleId: STANDARD_MODULES.sponsors.id,
            action: "manage_fields"
          }
        ])}
      />

      <Route
        name="EventLightTags"
        path="tags"
        getComponent={canUserView(EventLight.EventLightTags, null, null, [
          {
            moduleId: STANDARD_MODULES.contacts.id,
            action: "manage_fields"
          },
          {
            moduleId: STANDARD_MODULES.speakers.id,
            action: "manage_fields"
          },
          {
            moduleId: STANDARD_MODULES.exhibitors.id,
            action: "manage_fields"
          },
          {
            moduleId: STANDARD_MODULES.sponsors.id,
            action: "manage_fields"
          }
        ])}
      />

      {/* Workflows */}

      <Route
        name="EventLightWorkflows"
        path="workflows"
        getComponent={canUserView(
          EventLight.EventLightWorkflows,
          STANDARD_MODULES.settings.id,
          "update"
        )}
      />
      <Route
        name="EventLightWorkflowsBuilderNew"
        path="workflows/builder"
        getComponent={EventLight.EventLightWorkflowsBuilder}
      />
      <Route
        name="EventLightWorkflowsBuilder"
        path="workflows/builder/:workflowId"
        getComponent={EventLight.EventLightWorkflowsBuilder}
      />

      {/* */}

      <Route getComponent={EventLight.EventLightContactProfile}>
        <Route name="EventLightContactProfile" path="contact/:recordId" />
        <Route name="EventLightContactEdit" path="contact/:recordId/edit" />
        <Route
          name="EventLightContactHistory"
          path="contact/:recordId/history/:historyType"
        />
        <Route name="EventLightContactFiles" path="contact/:recordId/files" />
      </Route>

      <Route getComponent={EventLight.EventLightContactGroup}>
        <Route name="EventLightContactGroup" path="account/:recordId" />
        <Route
          name="EventLightContactGroupEdit"
          path="account/:recordId/edit"
        />
        <Route
          name="EventLightContactGroupHistory"
          path="account/:recordId/history"
        />
        <Route
          name="EventLightContactGroupFiles"
          path="account/:recordId/files"
        />
      </Route>
    </Route>

    <Route name="PlatformView" getComponent={Platform.PlatformView}>
      <Route name="HomeView" getComponent={Platform.HomeView} path="/home" />

      <Route name="accountSettings" getComponent={Account.SettingsHandler}>
        <Route
          name="accountSettingsSettings"
          path="/account/settings"
          getComponent={Account.SettingsSettingsHandler}
        />
      </Route>

      <Route name="platformNotFound" path="*" component={DefaultNotFound} />
    </Route>
  </Route>
);
